import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { formatNumber } from '@/utils/numberFormatter';

interface PdfBarChartProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  unit: string;
  width?: number;
  height?: number;
  chartId: string;
  yAxisLabel?: string;
}

const COLORS = [
  '#14b8a6', // teal-500
  '#0891b2', // cyan-600
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#ef4444', // red-500
  '#10b981', // emerald-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
];

/**
 * Bar chart component optimized for PDF rendering
 * This component renders without a Card wrapper for clean capture
 */
export const PdfBarChart = ({
  data,
  unit,
  width = 800,
  height = 450,
  chartId,
  yAxisLabel
}: PdfBarChartProps) => {
  // Calculate total
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div
      data-chart-id={chartId}
      className="bg-white p-4"
      style={{ width: `${width}px`, height: `${height + 80}px`, overflow: 'visible', position: 'relative' }}
    >
      <BarChart
        width={width}
        height={height}
        data={data}
        margin={{
          top: 20,
          right: 40,
          left: 70,
          bottom: 70,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 14, fill: '#374151', fontWeight: 500 }}
          angle={-20}
          textAnchor="end"
          height={70}
        />
        <YAxis
          tick={{ fontSize: 14, fill: '#374151' }}
          label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 14, fill: '#374151', fontWeight: 500 }
          } : undefined}
        />
        <Bar dataKey="value" radius={[8, 8, 0, 0]} animationDuration={0}>
          {data.map((entry, index: number) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color || COLORS[index % COLORS.length]}
            />
          ))}
        </Bar>
      </BarChart>

      {/* Total footer */}
      <div className="mt-2 px-5 py-2 bg-gray-100 rounded-lg flex justify-between items-center border border-gray-300">
        <span className="text-base font-semibold text-gray-800">Total:</span>
        <span className="text-lg font-bold text-gray-900">
          {formatNumber(total)} {unit}
        </span>
      </div>
    </div>
  );
};
